/**
 * Canonical status → color mappings.
 *
 * Every status in the app resolves to one semantic "variant" that controls
 * both the chip/badge background+text AND the companion dot colour.
 *
 * Usage:
 *   import { companyStatus, corporateActionStatus, archiveStatus } from '@/lib/status_colors'
 *
 *   // Badge classes (bg + text)
 *   <span className={companyStatus.badge('ACTIVE')}>Active</span>
 *
 *   // Dot colour only
 *   <span className={`w-1.5 h-1.5 rounded-full ${companyStatus.dot('ACTIVE')}`} />
 *
 *   // Variant key (for <Badge variant={...}>)
 *   <Badge variant={companyStatus.variant('ACTIVE')}>Active</Badge>
 */

import { IDENTITY_STATUS_LABEL, type IdentityStatus } from '@/lib/identity/identity-status'

// ---------------------------------------------------------------------------
// Variant type — mirrors badge.tsx's CVA variants
// ---------------------------------------------------------------------------

export type StatusVariant = 'default' | 'success' | 'warning' | 'destructive' | 'info' | 'outline'

// ---------------------------------------------------------------------------
// Variant → Tailwind class pairs
// ---------------------------------------------------------------------------

const BADGE_CLASSES: Record<StatusVariant, string> = {
  default:     'bg-primary-subtle text-primary-strong',
  success:     'bg-success-subtle text-success',
  warning:     'bg-warning-subtle text-warning',
  destructive: 'bg-danger-subtle text-danger',
  info:        'bg-info-subtle text-info',
  outline:     'bg-surface-muted text-text-muted',
}

const DOT_CLASSES: Record<StatusVariant, string> = {
  default:     'bg-primary',
  success:     'bg-success',
  warning:     'bg-warning',
  destructive: 'bg-danger',
  info:        'bg-info',
  outline:     'bg-text-subtle',
}

const SUMMARY_TEXT: Record<StatusVariant, string> = {
  default:     'text-primary',
  success:     'text-success',
  warning:     'text-warning',
  destructive: 'text-danger',
  info:        'text-info',
  outline:     'text-text-subtle',
}

// ---------------------------------------------------------------------------
// Helper to build a typed status resolver
// ---------------------------------------------------------------------------

/**
 * Every map below is keyed in UPPER_SNAKE_CASE, but several form-engine configs
 * (`src/form-engine/configs/*.ts`) submit/store lowercase enum values (e.g. `'active'`,
 * `'completed'`). `Record` lookup is exact-match, so an un-normalized status coming from one
 * of those forms silently misses every key and falls through to `fallback` — a status that
 * IS in the map (e.g. `'completed'`) renders identically to one that ISN'T (a typo), with no
 * error either way. Found 2026-09: this was why "Completed" rendered green via
 * `corporateActionStatus` (backend enum, already uppercase) but grey everywhere a
 * form-engine-sourced lowercase status reused the same map. Normalizing the lookup key here
 * fixes every current and future call site at once, instead of normalizing per-caller.
 */
function createStatusMap<S extends string>(
  map: Record<S, StatusVariant>,
  terminalStatuses: Set<string> = new Set(),
  fallback: StatusVariant = 'outline',
) {
  const normalize = (status: string): string => status.toUpperCase()
  // Normalize the map's OWN keys too — two existing maps (`riskStatus`, `severityStatus`) are
  // authored with lowercase keys, so only normalizing the incoming status (and leaving those
  // keys lowercase) would break them instead of fixing the uppercase-keyed maps.
  // (A plain Object.fromEntries(Object.entries(map).map(...)) widens to
  // Record<string, unknown> here — TS can't carry the value type through that round-trip on a
  // generic Record — so build it with an explicitly-typed reduce instead.)
  const upperMap = (Object.keys(map) as S[]).reduce<Record<string, StatusVariant>>(
    (acc, key) => {
      acc[normalize(key)] = map[key]
      return acc
    },
    {},
  )
  const upperTerminal = new Set([...terminalStatuses].map(normalize))

  const variant = (status: string | null | undefined): StatusVariant =>
    (status && upperMap[normalize(status)]) || fallback

  return {
    variant,
    badge:    (status: string | null | undefined): string => BADGE_CLASSES[variant(status)],
    dot:      (status: string | null | undefined): string => DOT_CLASSES[variant(status)],
    text:     (status: string | null | undefined): string => SUMMARY_TEXT[variant(status)],
    /** true for in-progress / active statuses → show dot; false for end states → no dot */
    isActive: (status: string | null | undefined): boolean =>
      !!status && !upperTerminal.has(normalize(status)),
    /**
     * The exact declared keys (not case-normalized) — for enum-coverage guards that need to know
     * whether a given member is DECLARED, as opposed to `variant()`, which cannot answer that: a
     * missing key and a key deliberately mapped to `outline` both return `'outline'` from
     * `variant()`, which is the exact ambiguity that made both the 2026-09 incident and C2
     * invisible. Not for status resolution — call sites use `variant`/`badge`/`dot`/`isActive`.
     */
    keys: (): string[] => Object.keys(map),
  }
}

// ---------------------------------------------------------------------------
// Canonical maps
// ---------------------------------------------------------------------------

/**
 * Legal entity / company status.
 *
 * `NOT_CONFIRMED` (T0004 ruling): neutral/info, never success — C5 (the feature that would
 * populate it) is parked and the member is inert, nothing writes it, so an entity showing this
 * status today is a bug, not a real state. `info` satisfies the T0083 enum-coverage guard without
 * asserting the entity is fine, and won't need to change colour when C5 eventually unparks. Not
 * terminal — it reads as unresolved/pending, not settled, so it keeps the "live" dot.
 */
export const companyStatus = createStatusMap(
  {
    ACTIVE:         'success',
    DORMANT:        'warning',
    DISSOLVED:      'destructive',
    IN_LIQUIDATION: 'destructive',
    STRUCK_OFF:     'destructive',
    SUSPENDED:      'warning',
    CONVERTED:      'info',
    CLOSED:         'destructive',
    NOT_CONFIRMED:  'info',
  },
  new Set(['DISSOLVED', 'STRUCK_OFF', 'CLOSED', 'CONVERTED']),
)

/** Board appointment status */
export const appointmentStatus = createStatusMap(
  {
    ACTIVE:   'success',
    RESIGNED: 'outline',
    REMOVED:  'destructive',
    EXPIRED:  'warning',
  },
  new Set(['RESIGNED', 'REMOVED', 'EXPIRED']),
)

/** Corporate action / compliance status */
export const corporateActionStatus = createStatusMap(
  {
    PENDING:     'warning',
    IN_PROGRESS: 'info',
    COMPLETED:   'success',
    CANCELLED:   'outline',
  },
  new Set(['COMPLETED', 'CANCELLED']),
)

/** Active vs archived (persons, entities) */
export const archiveStatus = createStatusMap(
  {
    ACTIVE:   'success',
    ARCHIVED: 'warning',
  },
  new Set(['ARCHIVED']),
)

/** Engagement lifecycle status (India company registration) */
export const engagementStatus = createStatusMap(
  {
    ACTIVE:      'info',
    IN_PROGRESS: 'info',
    BLOCKED:     'warning',
    COMPLETED:   'success',
    CANCELLED:   'destructive',
    FAILED:      'destructive',
  },
  new Set(['COMPLETED', 'CANCELLED', 'FAILED']),
)

/** Engagement registration-step execution status (handover v2 §3 colour guide) */
export const engagementStepStatus = createStatusMap(
  {
    IN_PROGRESS:        'info',
    COMPLETED:          'success',
    FAILED:             'destructive',
    EXPIRED:            'destructive', // red clock — deadline lapsed
    WAITING_VENDOR:     'warning',
    WAITING_REVIEW:     'warning',
    WAITING_CLIENT:     'warning',
    WAITING_GOVERNMENT: 'warning',
    WAITING_DEPENDENCY: 'outline',     // neutral — waiting for previous steps
    SKIPPED:            'outline',     // muted dash — not applicable
    OVERRIDDEN:         'success',     // admin override (badge conveys the override note)
  },
  new Set(['COMPLETED', 'FAILED', 'SKIPPED', 'EXPIRED', 'OVERRIDDEN']),
)

/**
 * Agreement lifecycle status (contract-svc, B2B agreements).
 *
 * These keys are EXACTLY contract-svc's `AgreementDisplayStatus` — the server-derived collapse of
 * (contractStatus, contractSubStatus, effectiveTo) that arrives as `Agreement.displayStatus`. Keep them
 * in step with `src/lib/contracts/agreement-status.ts`, which mirrors the same enum.
 *
 * `DRAFT` and `SIGNED` were here from the wireframe mock and are gone deliberately: neither is a real
 * agreement state. There is no draft stage (an agreement is created already awaiting signature), and a
 * signed agreement reads `ACTIVE` — or `EXPIRING_SOON`/`EXPIRED` once its term is in view.
 *
 * AWAITING_SIGNATURE/SENT/ACTIVE/EXPIRING_SOON are live (dot); DECLINED/EXPIRED/INACTIVE are terminal.
 */
export const agreementStatus = createStatusMap(
  {
    AWAITING_SIGNATURE: 'warning',
    SENT:               'info',
    ACTIVE:             'success',
    EXPIRING_SOON:      'warning',
    EXPIRED:            'destructive',
    DECLINED:           'destructive',
    INACTIVE:           'outline',
  },
  new Set(['DECLINED', 'INACTIVE', 'EXPIRED']),
)

/** Alias — same resolver, exposed under the `contract*` name for direct badge/dot use. */
export const contractStatus = agreementStatus

/** Per-signer e-signature status (used on the agreement Signing tab). */
export const signerStatus = createStatusMap(
  {
    PENDING:  'warning',
    VIEWED:   'info',
    SIGNED:   'success',
    DECLINED: 'destructive',
  },
  new Set(['SIGNED', 'DECLINED']),
)

/** Risk severity */
export const riskStatus = createStatusMap({
  low:      'success',
  medium:   'warning',
  high:     'destructive',
  critical: 'destructive',
})

/** Alert/error severity */
export const severityStatus = createStatusMap({
  success: 'success',
  info:    'info',
  warning: 'warning',
  error:   'destructive',
})

/**
 * WhatsApp business-template provider status (form-svc's projection of the Meta approval
 * state, `ChannelTemplateVariantModel.providerStatus` / `WhatsAppTemplateVersionModel.status`).
 *
 * DRAFT (never submitted) and RETIRED (deliberately torn down) are both "nothing is
 * happening" states — no dot. PENDING/SUBMITTING/RETIRING are in-flight (dot, warning).
 * APPROVED is the long-lived sendable state (dot, success) — same treatment as an ACTIVE
 * company/entity elsewhere in this file. REJECTED/DISABLED are terminal failures (no dot,
 * danger). FLAGGED/PAUSED are reversible quality holds Meta can lift on its own — treated
 * as still "live" (dot) but cautionary (warning), not a hard failure.
 */
export const whatsAppTemplateStatus = createStatusMap(
  {
    DRAFT:      'outline',
    SUBMITTING: 'warning',
    PENDING:    'warning',
    APPROVED:   'success',
    REJECTED:   'destructive',
    PAUSED:     'warning',
    FLAGGED:    'destructive',
    DISABLED:   'destructive',
    RETIRING:   'warning',
    RETIRED:    'outline',
  },
  new Set(['DRAFT', 'REJECTED', 'DISABLED', 'RETIRED']),
)

/** Share plan (ESOP/RSU/etc.) lifecycle status. DRAFT hasn't started yet → no dot, same treatment as `whatsAppTemplateStatus`'s DRAFT. */
export const sharePlanStatus = createStatusMap(
  {
    ACTIVE: 'success',
    CLOSED: 'outline',
    DRAFT:  'outline',
  },
  new Set(['CLOSED', 'DRAFT']),
)

/** Committee lifecycle status. */
export const committeeStatus = createStatusMap(
  {
    ACTIVE:    'success',
    DISSOLVED: 'destructive',
  },
  new Set(['DISSOLVED']),
)

/** Signing-authority (bank/cheque mandate) status. Deliberately separate from `appointmentStatus` — REVOKED has no equivalent there. */
export const signingAuthorityStatus = createStatusMap(
  {
    ACTIVE:  'success',
    REVOKED: 'destructive',
  },
  new Set(['REVOKED']),
)

/**
 * Share-plan interest (an individual's option/RSU grant) status.
 * GRANTED/PARTIALLY_VESTED/FULLY_VESTED are live holdings (dot) — FULLY_VESTED gets the same
 * long-lived-success treatment as `whatsAppTemplateStatus`'s APPROVED. EXERCISED (converted to
 * shares) and LAPSED (forfeited) are true end states.
 */
export const planInterestStatus = createStatusMap(
  {
    GRANTED:          'info',
    PARTIALLY_VESTED: 'warning',
    FULLY_VESTED:     'success',
    EXERCISED:        'success',
    LAPSED:           'destructive',
  },
  new Set(['EXERCISED', 'LAPSED']),
)

/** Entity review (periodic compliance review) status. Note the form-engine value is `complete`, not `completed`. */
export const entityReviewStatus = createStatusMap(
  {
    SCHEDULED:   'warning',
    IN_PROGRESS: 'info',
    COMPLETE:    'success',
    CANCELLED:   'outline',
  },
  new Set(['COMPLETE', 'CANCELLED']),
)

/** Data-library task/item status. OVERDUE stays "live" (dot) — it's still outstanding work, just late. */
export const dataLibraryItemStatus = createStatusMap(
  {
    PENDING:     'warning',
    IN_PROGRESS: 'info',
    COMPLETE:    'success',
    OVERDUE:     'destructive',
    CANCELLED:   'outline',
  },
  new Set(['COMPLETE', 'CANCELLED']),
)

/**
 * Report run status (W3.T3.1).
 *
 * Replaces the inline STATUS_VARIANT map that RecentReportsList declared for itself — the fifth
 * independent status system in the app, and the smallest.
 */
export const reportRunStatus = createStatusMap(
  {
    COMPLETED: 'success',
    FAILED:    'destructive',
    PENDING:   'info',
    RUNNING:   'info',
  },
  new Set(['COMPLETED', 'FAILED']),
)

/**
 * Integration background-job lifecycle.
 *
 * This used to be a bespoke pill map in the Integrations page, so the same
 * terminal "Completed" state rendered differently from Invoices, Compliance,
 * and Reports. Keep the vocabulary here so every integration connector uses
 * the platform status primitive and the active/terminal shape convention.
 */
export const integrationJobStatus = createStatusMap(
  {
    QUEUED:      'outline',
    IN_PROGRESS: 'info',
    COMPLETED:   'success',
    FAILED:      'destructive',
    CANCELLED:   'outline',
  },
  new Set(['COMPLETED', 'FAILED', 'CANCELLED']),
)

/**
 * Integration connection lifecycle. The backend enum is the sole lifecycle
 * source; frontend surfaces only resolve its semantic colour and whether the
 * state is live (dot chip) or terminal (plain badge).
 */
export const integrationConnectionStatus = createStatusMap(
  {
    SETUP_PENDING:         'warning',
    SYNCING_INITIAL:       'info',
    INITIAL_SYNC_COMPLETE: 'success',
    INITIAL_SYNC_PARTIAL:  'warning',
    INITIAL_SYNC_FAILED:   'destructive',
    MAPPING_IN_PROGRESS:   'warning',
    ACTIVE:                'success',
    INACTIVE:              'outline',
    REAUTH_REQUIRED:       'warning',
    DISCONNECTED:          'destructive',
  },
  new Set(['DISCONNECTED']),
)

/** Integration connector availability shown in the marketplace catalog. */
export const integrationConnectorStatus = createStatusMap(
  {
    ACTIVE:     'success',
    BETA:       'warning',
    DEPRECATED: 'outline',
    DISABLED:   'outline',
  },
  new Set(['DEPRECATED', 'DISABLED']),
)

/**
 * A client-portfolio import's lifecycle (bulk-import-client-portfolios). Amber marks the two states
 * waiting on the importer: a review to confirm, and failed rows to retry.
 */
export const fileImportStatus = createStatusMap(
  {
    PENDING_UPLOAD:        'outline',
    UPLOADED:              'info',
    PARSING:               'info',
    VALIDATING:            'info',
    AWAITING_CONFIRMATION: 'warning',
    COMMITTING:            'info',
    COMPLETED:             'success',
    PARTIALLY_COMMITTED:   'warning',
    FAILED:                'destructive',
    INVALID:               'destructive',
    CANCELLED:             'outline',
    EXPIRED:               'outline',
  },
  new Set(['COMPLETED', 'PARTIALLY_COMMITTED', 'FAILED', 'INVALID', 'CANCELLED', 'EXPIRED']),
)

/**
 * An Agency CRM proposal's lifecycle (ENT-565 ZDP-T37). The eleven states the server defines
 * (crm-svc `CRM_PROPOSAL_STATES`); the FE only colours them, it never derives one. Amber marks the
 * state waiting on an approver; the settled end states carry no liveness dot.
 */
export const proposalStatus = createStatusMap(
  {
    AWAITING_INPUT:    'info',
    INPUT_IN_PROGRESS: 'info',
    INPUT_SUBMITTED:   'info',
    RENDERED:          'info',
    PENDING_APPROVAL:  'warning',
    APPROVED:          'success',
    REJECTED:          'destructive',
    WITHDRAWN:         'outline',
    ACCEPTED:          'success',
    EXPIRED:           'outline',
    CANCELLED:         'outline',
  },
  new Set(['REJECTED', 'WITHDRAWN', 'ACCEPTED', 'EXPIRED', 'CANCELLED']),
)

/**
 * Client/vendor activation state (W3.T3.1).
 *
 * Two values only — REJECTED/DECLINED bucket into ONBOARDING at the gateway (owner decision,
 * T0029). Absent renders nothing at all, which is the caller's job, not this map's.
 */
export const activationState = createStatusMap(
  {
    ACTIVE:     'success',
    ONBOARDING: 'warning',
  },
  new Set([]),
)

/**
 * Client lifecycle (EW-FIX-08) — `RelatedOrg.lifecycleState`, the FIVE-state reading of a client's activation that
 * the BE derives once (`ClientLifecycleState`: COMPLETE, NOT_STARTED, ONBOARDING, READY_FOR_REVIEW, REJECTED).
 *
 * NOT `activationState` above: that one has two values because the gateway collapses REJECTED and READY_FOR_REVIEW into
 * ONBOARDING, which is the defect this map exists to fix on the Clients listing (the thing needing attention read as the
 * thing already in progress, and a client with no activation row rendered NO label). A separate map, not a widened one:
 * `activationState` has six other consumers that never receive the new values.
 *
 * Dot: COMPLETE, ONBOARDING and READY_FOR_REVIEW keep the live dot the shipped two-state chip showed for ACTIVE and
 * ONBOARDING (visual continuity). NOT_STARTED and REJECTED are settled states with nothing in progress, so no dot.
 * (ASSUMED, not ruled: docs/tasks/EW-FIX-08-client-listing-five-state-row-label.md.)
 *
 * A NULL lifecycleState is not in this map and is not rendered by it: the caller renders nothing (ASSUMED, not ruled).
 * Guarded against the generated enum in status_colors.test.ts: a sixth BE state with no key here fails the build.
 */
export const clientLifecycleState = createStatusMap(
  {
    COMPLETE:         'success',
    ONBOARDING:       'warning',
    READY_FOR_REVIEW: 'info',
    NOT_STARTED:      'default',
    REJECTED:         'destructive',
  },
  new Set(['NOT_STARTED', 'REJECTED']),
)

/**
 * Invoice status (W3.T3.1).
 *
 * The VARIANT half of what pages/Invoices/invoice-utils.ts used to own alone. Labels stay there:
 * several are not derivable by title-casing the enum ("Ready to raise", "Raised by manager"), and
 * the server can override any of them via `displayStatus`. So the split is deliberate —
 * status_colors owns colour, invoice-utils owns wording — and callers pass the label through
 * StatusIndicator's `label` prop.
 *
 * invoice-utils used a `neutral`/`danger` vocabulary; this maps onto the canonical
 * `outline`/`destructive` used by every other status map.
 */
export const invoiceStatus = createStatusMap(
  {
    DRAFT:             'outline',
    READY_TO_RAISE:    'outline',
    SUBMITTED:         'info',
    MANAGER_RAISED:    'info',
    MANAGER_APPROVED:  'warning',
    MANAGER_REJECTED:  'destructive',
    ISSUED:            'info',
    RECEIVED:          'info',
    PENDING:           'outline',
    PROCESSING:        'info',
    APPROVED:          'warning',
    VERIFIED:          'warning',
    REJECTED:          'destructive',
    PAYOUT_SCHEDULED:  'warning',
    PAYMENT_INITIATED: 'warning',
    PAYMENT_RECEIVED:  'success',
    PAYMENT_FAILED:    'destructive',
    PARTIALLY_PAID:    'warning',
    PAID:              'success',
    OVERDUE:           'destructive',
    HOLD:              'warning',
    RECALLED:          'outline',
    EXPIRED:           'outline',
    CANCELLED:         'outline',
    VOID:              'outline',
  },
  new Set(['PAID', 'PAYMENT_RECEIVED', 'REJECTED', 'MANAGER_REJECTED', 'CANCELLED', 'VOID', 'EXPIRED', 'RECALLED']),
)

/**
 * EW-ICT-T05 (invoice Wave 2) — resolved-view provenance for the three catalogue-admin entities
 * (line-item categories, service-catalogue entries, tax rules): INHERITED (platform baseline,
 * untouched), OVERRIDDEN (tenant has its own active row for this key), SUPPRESSED (tenant
 * deactivated its own row, hiding the baseline), TENANT_ONLY (tenant-authored, no baseline exists).
 * This is a CLASSIFICATION, not a lifecycle — none of the four represents "in progress" the way
 * ACTIVE/PENDING do elsewhere in this file, so all four are terminal (plain Badge, never a dot).
 */
export const invoiceCatalogueProvenanceStatus = createStatusMap(
  {
    INHERITED:   'info',
    OVERRIDDEN:  'success',
    SUPPRESSED:  'warning',
    TENANT_ONLY: 'default',
  },
  new Set(['INHERITED', 'OVERRIDDEN', 'SUPPRESSED', 'TENANT_ONLY']),
)

/**
 * Natural-person identity status (F1 M2 identity slice, PRD FR-F3, DD Decision 8).
 *
 * `LINKED` is the healthy ongoing state (dot, success) — the person's identity is correctly tied
 * to a canonical login. `NO_EMAIL_PENDING`/`CONFLICT_PENDING` are the two statuses FR-16(a,e) and
 * `IDENTITY_STATUS_LABEL.blocked` mark as blocking new appointments/worker records/contacts/
 * memberships — both stay "live" (dot): something is still wrong and actionable
 * (`CaptureEmailDialog`/`IdentityBlockedBanner` exist to resolve them), with `CONFLICT_PENDING`
 * the more severe of the two (a shared-email conflict needs platform/tenant-admin resolution, not
 * just an email capture) so it reads `destructive` where `NO_EMAIL_PENDING` reads `warning`.
 * `NO_EMAIL_ARCHIVED` and `UNLINKED` are both settled/inert (no dot) — nothing is currently
 * pending for either — with `NO_EMAIL_ARCHIVED` kept visually distinct (`warning`, since the
 * person was archived specifically for lacking an email) from `UNLINKED`'s neutral `outline`
 * (never had/needed a login identity). Labels come from `IDENTITY_STATUS_LABEL`
 * (`src/lib/identity/identity-status.ts`), the DD's single source of truth for status copy.
 */
export const identityStatus = createStatusMap<IdentityStatus>(
  {
    LINKED: 'success',
    NO_EMAIL_PENDING: 'warning',
    CONFLICT_PENDING: 'destructive',
    NO_EMAIL_ARCHIVED: 'warning',
    UNLINKED: 'outline',
  },
  new Set(['NO_EMAIL_ARCHIVED', 'UNLINKED']),
)

/**
 * Team-member invite acceptance status (PRD FR-F5): `TeamMembers.acceptanceStatus`.
 * `PENDING` (invited, not yet accepted) stays live (dot, warning) — it is still awaiting the
 * invitee's action. `ACCEPTED` is the settled end state (no dot, success). Per FR-F5 the label for
 * `PENDING` is "Invited", not the generic title-cased "Pending" — see
 * `formatAcceptanceStatusLabel`/`ACCEPTANCE_STATUS_LABELS` below; `ACCEPTED` keeps the generic
 * formatter's "Accepted" (FR-F5 says the caller renders nothing for `ACCEPTED`, but the resolver
 * and label stay exhaustive so the type itself is never a special case).
 */
export const acceptanceStatus = createStatusMap(
  {
    PENDING: 'warning',
    ACCEPTED: 'success',
  },
  new Set(['ACCEPTED']),
)

/**
 * Team add-on permission grant status (ENT-566 P0d T01, FR-D1): `PermissionGrant.status`, derived
 * server-side from the database clock — never re-derived client-side from `effectiveFrom`/
 * `effectiveTo` against the browser clock (see the T01/T02 handovers). `SCHEDULED` stays live (dot,
 * info — the window has not opened yet, nothing wrong). `ACTIVE` stays live (dot, success).
 * `EXPIRED` is terminal (no dot, the map's own `outline`/muted fallback) — an ended window, settled.
 * An unknown value is NOT in the terminal set, so it still renders live (dot present) — just with
 * the `outline`/muted colour via the same fallback — matching every other resolver in this file.
 */
export const permissionGrantStatus = createStatusMap(
  {
    SCHEDULED: 'info',
    ACTIVE: 'success',
    EXPIRED: 'outline',
  },
  new Set(['EXPIRED']),
)

/** Label overrides for WhatsApp provider statuses whose generic title-case reads awkwardly. */
const WHATSAPP_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending review',
}

/* ─── Time tracking (PRD time-tracking-module.md §2, T02) ───────────────────
   Three families, not one. They are genuinely different vocabularies and
   collapsing them would force a single map to hold both `Submitted` (a leave
   request awaiting a decision) and `Submitted` (a timesheet period handed in) —
   which share a word and not a meaning.

   `createStatusMap` normalizes case on both sides, so a lowercase form value and
   an uppercase backend enum resolve identically. That normalization is why these
   maps can be authored in the backend's UPPER_SNAKE_CASE without every call site
   having to know which shape it received. */

/** A leave request's lifecycle (PRD §2.9 filter catalogue — the five real values). */
export const timeOffStatus = createStatusMap(
  {
    /* The live contract's awaiting-decision state (time-tracking-svc `TimeTrackingTimeOffStatus`). */
    PENDING:                'warning',
    SUBMITTED:              'warning',
    APPROVED:               'success',
    REJECTED:               'destructive',
    CANCELLED:              'outline',
    /* Not a delete. A worker has asked to withdraw an already-approved request and an
       approver must act on it, so it is ACTIVE and drives its own row actions (PRD F9). */
    CANCELLATION_IN_REVIEW: 'info',
  },
  new Set(['APPROVED', 'REJECTED', 'CANCELLED']),
)

/** A timesheet period's lifecycle (PRD §2.3-§2.5). */
export const timesheetPeriodStatus = createStatusMap(
  {
    DRAFT:      'outline',
    SUBMITTED:  'warning',
    APPROVED:   'success',
    REJECTED:   'destructive',
    BILLED:     'info',
    PAID:       'success',
    CANCELLED:  'outline',
  },
  new Set(['APPROVED', 'REJECTED', 'BILLED', 'PAID', 'CANCELLED']),
)

/**
 * Cycles' own FE-derived two-value status (TT-V11 Phase 1, T06 — `deriveTimesheetStatus`), NOT the
 * log-status vocabulary `timesheetPeriodStatus` above uses (no BE `status` field exists on
 * `TimeTrackingTimeSheet` at all — see that function's own docblock). `Current` is the ongoing
 * period (dot, in-progress); `Paid` is settled (no dot).
 */
export const cycleStatus = createStatusMap(
  {
    Current: 'warning',
    Paid:    'success',
  },
  new Set(['Paid']),
)

/**
 * A worker's Time enrollment readiness (TT-V11 stub screens, `EnrollmentPanel`): READY when every
 * slot activation needs is assigned, PENDING otherwise. Neither is terminal — a ready worker can lose
 * an assignment — so both render as live chips.
 */
export const enrollmentStatus = createStatusMap(
  {
    READY:    'success',
    PENDING:  'warning',
    /* A leave-policy enrolment request's decision (TT-V11 fixture sweep WF3, `TimeTrackingEnrolmentRequestStatus`). */
    APPROVED: 'success',
    REJECTED: 'destructive',
    /* LPH-T35: the requester withdrew a PENDING request; the row is kept and listed. Neutral, and an end state. */
    WITHDRAWN: 'outline',
  },
  new Set<string>(['APPROVED', 'REJECTED', 'WITHDRAWN']),
)

/**
 * A holiday calendar's lifecycle (LPH-T23, `TimeTrackingHolidayCalendarStatus`): created DRAFT, published ACTIVE, ARCHIVED
 * for good (no delete). ARCHIVED is the one end state.
 */
export const holidayCalendarStatus = createStatusMap(
  {
    DRAFT:    'warning',
    ACTIVE:   'success',
    ARCHIVED: 'outline',
  },
  new Set<string>(['ARCHIVED']),
)

/**
 * A Time policy's publication state (TT-V11 stub screens, `PolicyCard`). Only ACTIVE policies exist in
 * the sample data; DRAFT and RETIRED are mapped so a live surface's first non-active policy does not
 * fall through to the unknown-status outline.
 */
export const policyStatus = createStatusMap(
  {
    ACTIVE:  'success',
    DRAFT:   'outline',
    RETIRED: 'outline',
  },
  new Set(['RETIRED']),
)

/** A single time log's lifecycle (PRD §2.8 columns + the hours-bar groups, §5.1). */
export const timeLogStatus = createStatusMap(
  {
    DRAFT:      'outline',
    /**
     * The real schema's pending state (`TimeTrackingTimeLogStatus`: APPROVED | BILLED | CANCELLED |
     * DRAFT | PAID | REJECTED | **SUBMITTED**) — and it was MISSING from this map, so every
     * submitted log fell through to the `outline` fallback and rendered neutral grey. The one state
     * that actually needs a decision was the least visible thing on the screen.
     *
     * The map was authored against the pre-live fixture vocabulary, where the pending bucket was
     * called `UNAPPROVED` — a string that does not exist anywhere in the live schema (confirmed by
     * SDL introspection at T29 §7.2, and again here against the generated types). `UNAPPROVED` is
     * kept below because `hours-groups.ts` still uses it as the BUCKET name it maps `SUBMITTED`
     * onto for the hours bar; it is not a status a log can hold.
     *
     * Found in the visual re-review (EW-FIX-62), not by a test — every test asserted on the
     * fixture vocabulary too, so the gap was invisible from inside the suite.
     */
    SUBMITTED:  'warning',
    UNAPPROVED: 'warning',
    APPROVED:   'success',
    REJECTED:   'destructive',
    BILLED:     'info',
    PAID:       'success',
    CANCELLED:  'outline',
    /* Overtime is a *kind* of logged hour that the hours bar tints separately; it is
       never terminal, because overtime still travels through approval like any hour. */
    OVERTIME:   'info',
    /* NOT a zero-hours day — "this person was not engaged on this date" (ruling G6). The BE
       densifies the day-wise read against the arrangement's effectiveFrom/effectiveTo and returns
       this rather than an absent row, because absence was indistinguishable from "logged nothing"
       and made period totals lie at onboarding/offboarding boundaries.
       ⚠️ Render blank/greyed and NEVER sum it into a total. */
    NOT_ENGAGED: 'outline',
  },
  new Set(['APPROVED', 'REJECTED', 'BILLED', 'PAID', 'CANCELLED']),
)

export function formatWhatsAppStatusLabel(status: string | null | undefined): string {
  if (!status) return '—'
  return WHATSAPP_STATUS_LABELS[status] ?? formatStatusLabel(status)
}

/**
 * A compliance sign-off step — a task-framework subtask stamped with `meta.stepKey`. The engine
 * opens one step at a time: PENDING (not reached yet) → OPEN (awaiting this approver) →
 * APPROVED | REJECTED, or OPEN ⇄ CHANGES_REQUESTED while the owner reworks it. A rejection fails the whole obligation, hence `destructive`.
 */
export const signoffStepStatus = createStatusMap(
  {
    PENDING:  'outline',
    OPEN:     'warning',
    /* Returned to the owner for rework (TF seed v30) — live, not terminal. */
    CHANGES_REQUESTED: 'warning',
    APPROVED: 'success',
    REJECTED: 'destructive',
  },
  new Set(['APPROVED', 'REJECTED']),
)

const SIGNOFF_STEP_LABELS: Record<string, string> = {
  PENDING: 'Not started',
  OPEN: 'Awaiting approval',
  CHANGES_REQUESTED: 'Changes requested',
}

export function formatSignoffStepLabel(status: string | null | undefined): string {
  if (!status) return '\u2014'
  return SIGNOFF_STEP_LABELS[status.toUpperCase()] ?? formatStatusLabel(status)
}

/**
 * An MCA portal-filing subtask (IDC-T12): PENDING → OPENING (session starting) → OPEN (live,
 * operator action needed) → CAPTURED (SRN recorded) | FAILED.
 */
export const portalFilingStatus = createStatusMap(
  {
    PENDING:  'outline',
    OPENING:  'info',
    OPEN:     'warning',
    CAPTURED: 'success',
    FAILED:   'destructive',
  },
  new Set(['CAPTURED', 'FAILED']),
)

const PORTAL_FILING_LABELS: Record<string, string> = {
  PENDING: 'Not started',
  OPENING: 'Opening session',
  OPEN: 'Action needed',
  CAPTURED: 'Filed',
}

export function formatPortalFilingLabel(status: string | null | undefined): string {
  if (!status) return '\u2014'
  return PORTAL_FILING_LABELS[status.toUpperCase()] ?? formatStatusLabel(status)
}

/**
 * `identityStatus` label \u2014 reads `IDENTITY_STATUS_LABEL` (`src/lib/identity/identity-status.ts`),
 * the DD's single source of truth for identity-status copy, so the label is never duplicated here.
 * An unrecognised value (not one of the five declared `IdentityStatus` members) falls through to
 * the generic title-case formatter, same fallback behaviour as every other type.
 */
export function formatIdentityStatusLabel(status: string | null | undefined): string {
  if (!status) return '\u2014'
  const upper = status.toUpperCase() as IdentityStatus
  return IDENTITY_STATUS_LABEL[upper]?.label ?? formatStatusLabel(status)
}

/** FR-F5: `PENDING` reads "Invited" (an invite awaiting acceptance), not the generic "Pending". */
const ACCEPTANCE_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Invited',
}

export function formatAcceptanceStatusLabel(status: string | null | undefined): string {
  if (!status) return '\u2014'
  return ACCEPTANCE_STATUS_LABELS[status.toUpperCase()] ?? formatStatusLabel(status)
}

// ---------------------------------------------------------------------------
// Shared label formatter  (ACTIVE → Active, IN_PROGRESS → In Progress)
// ---------------------------------------------------------------------------

export function formatStatusLabel(status: string | null | undefined): string {
  if (!status) return '\u2014'
  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase())
}
